import { useMemo } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// project imports
import AppBarStyled from './AppBarStyled';
import HeaderContent from './HeaderContent';
import IconButton from 'components/@extended/IconButton';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';

// assets
import MenuFoldOutlined from '@ant-design/icons/MenuFoldOutlined';
import MenuUnfoldOutlined from '@ant-design/icons/MenuUnfoldOutlined';

// ==============================|| MAIN LAYOUT - HEADER ||============================== //

export default function Header() {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  // header content
  const headerContent = useMemo(() => <HeaderContent />, []);

  // common header
  const mainHeader = (
    <Toolbar
      sx={{
        // White text inside the toolbar (the visible green bar)
        '& .MuiTypography-root': { color: '#fff' },
        '& .MuiSvgIcon-root': { color: '#fff' },
        '& .anticon': { color: '#fff' },
        '& .MuiButtonBase-root': { color: '#fff' },
        // Profile button styling — the pill with avatar + name
        '& .MuiButtonBase-root[aria-haspopup="true"]': {
          bgcolor: 'rgba(255,255,255,0.18)',
          backdropFilter: 'blur(8px)',
          borderRadius: '24px',
          px: 1.5,
          py: 0.5,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' }
        }
      }}
    >
      <IconButton
        aria-label="open drawer"
        onClick={() => handlerDrawerOpen(!drawerOpen)}
        edge="start"
        color="secondary"
        variant="light"
        sx={{
          color: '#fff',
          bgcolor: drawerOpen ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.22)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' },
          ml: { xs: 0, lg: -2 }
        }}
      >
        {!drawerOpen ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </IconButton>

      {/* Tagline — beside the drawer toggle; takes available width, no clipping */}
      <Typography
        variant="h6"
        sx={{
          display: { xs: 'none', sm: 'block' },
          flex: 1,
          minWidth: 0,
          ml: 1.5,
          mr: 1,
          fontWeight: 600,
          letterSpacing: '0.04em',
          whiteSpace: 'nowrap',
          fontSize: { sm: '0.95rem', md: '1.1rem' },
          color: 'rgba(255,255,255,0.92)',
          textShadow: '0 1px 2px rgba(0,0,0,0.18)'
        }}
      >
        Future of Farming Technology
      </Typography>

      {headerContent}
    </Toolbar>
  );

  const appBar = {
    position: 'fixed',
    color: 'inherit',
    elevation: 0,
    sx: {
      borderBottom: 'none',
      zIndex: 1200,
      width: { xs: '100%', lg: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : `calc(100% - ${MINI_DRAWER_WIDTH}px)` },
      // Agri green gradient matching the theme primary palette
      background: 'linear-gradient(135deg, #16a34a 0%, #15803d 50%, #052e16 100%)',
      boxShadow: '0 2px 12px rgba(5, 46, 22, 0.25)'
    }
  };

  return (
    <>
      {/* GLOBAL OVERRIDE — forces dark text inside any popover/popper/menu rendered outside the toolbar */}
      <Box
        component="style"
        dangerouslySetInnerHTML={{
          __html: `
            /* Catch all popover/popper/menu variants */
            .MuiPopper-root .MuiTypography-root,
            .MuiPopover-root .MuiTypography-root,
            .MuiMenu-root .MuiTypography-root,
            .MuiMenuItem-root .MuiTypography-root,
            .MuiPopper-root h1, .MuiPopper-root h2, .MuiPopper-root h3,
            .MuiPopper-root h4, .MuiPopper-root h5, .MuiPopper-root h6,
            .MuiPopover-root h1, .MuiPopover-root h2, .MuiPopover-root h3,
            .MuiPopover-root h4, .MuiPopover-root h5, .MuiPopover-root h6,
            .MuiPopper-root p, .MuiPopper-root span:not(.MuiSwitch-thumb):not(.MuiSwitch-track),
            .MuiPopover-root p, .MuiPopover-root span:not(.MuiSwitch-thumb):not(.MuiSwitch-track) {
              color: rgba(0, 0, 0, 0.87) !important;
            }

            /* Secondary text — captions, body2, role labels */
            .MuiPopper-root .MuiTypography-caption,
            .MuiPopper-root .MuiTypography-body2,
            .MuiPopover-root .MuiTypography-caption,
            .MuiPopover-root .MuiTypography-body2 {
              color: rgba(0, 0, 0, 0.6) !important;
            }

            /* Icons inside dropdowns — keep their own colors (don't force black) */
            /* But ensure ListItem text is dark */
            .MuiPopper-root .MuiListItemText-root .MuiTypography-root,
            .MuiPopover-root .MuiListItemText-root .MuiTypography-root {
              color: rgba(0, 0, 0, 0.87) !important;
            }

            /* Tabs in dropdown */
            .MuiPopper-root .MuiTab-root,
            .MuiPopover-root .MuiTab-root {
              color: rgba(0, 0, 0, 0.6) !important;
            }
            .MuiPopper-root .MuiTab-root.Mui-selected,
            .MuiPopover-root .MuiTab-root.Mui-selected {
              color: #15803d !important;
            }

            /* Buttons inside the dropdown — primary green */
            .MuiPopper-root .MuiButton-text,
            .MuiPopover-root .MuiButton-text {
              color: #15803d !important;
            }

            /* IconButton inside dropdown — dark icons, green on hover */
            .MuiPopper-root .MuiIconButton-root,
            .MuiPopover-root .MuiIconButton-root {
              color: rgba(0, 0, 0, 0.54) !important;
            }
            .MuiPopper-root .MuiIconButton-root .MuiSvgIcon-root,
            .MuiPopover-root .MuiIconButton-root .MuiSvgIcon-root,
            .MuiPopper-root .MuiIconButton-root .anticon,
            .MuiPopover-root .MuiIconButton-root .anticon {
              color: rgba(0, 0, 0, 0.54) !important;
            }
            .MuiPopper-root .MuiIconButton-root:hover,
            .MuiPopover-root .MuiIconButton-root:hover {
              color: #15803d !important;
            }
          `
        }}
      />
      {!downLG ? (
        <AppBarStyled open={drawerOpen} {...appBar}>
          {mainHeader}
        </AppBarStyled>
      ) : (
        <AppBar {...appBar}>{mainHeader}</AppBar>
      )}
    </>
  );
}